import { useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/**
 * A live boost price (ENG-677) from
 * `GET /public/bands/{id}/queue/{entryId}/boost-quote` (ENG-678).
 *
 * `request` is `{ target }` (a rail spot), `{ amount }` (dollars to add) or
 * `{}` (the default spot, one up). `queueKey` is anything that changes when
 * the queue does (the bootstrap's queue), so the desk re-quotes then rather
 * than showing a price the queue has moved past. Answers that arrive after a
 * newer request are dropped.
 */
export const useBoostQuote = (bandId, entryId, request, queueKey) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const target = request?.target;
  const amount = request?.amount;

  useEffect(() => {
    if (!bandId || !entryId) {
      setQuote(null);
      return undefined;
    }
    const seq = ++seqRef.current;
    const params = new URLSearchParams();
    if (target != null) params.set('target', String(target));
    else if (amount != null) params.set('amount', String(amount));
    const query = params.toString();
    setLoading(true);
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandId}/queue/${entryId}/boost-quote${query ? `?${query}` : ''}`)
      .then((answer) => {
        if (seq !== seqRef.current) return;
        setQuote(answer);
        setError(null);
      })
      .catch((err) => {
        if (seq !== seqRef.current) return;
        setError(err?.code || 'boost_quote_failed');
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
    return () => { seqRef.current += 1; };
  }, [bandId, entryId, target, amount, queueKey]);

  return { quote, error, loading };
};
